import { filter } from 'graphql-anywhere'
import debounce from 'lodash/debounce'
import toLower from 'lodash/toLower'
import Head from 'next/head'
import { useEffect } from 'react'

import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { useLogExperiment } from 'experiments/hooks/useExperiment'
import { useStartExperiment } from 'experiments/hooks/useStartExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useTikTokPixelTrackEvent } from 'head/hooks/useTikTokPixelTrackEvent'
import { buildPDPMetaDescription, buildTitle } from 'head/utils/seoHelpers'
import Cookies from 'js-cookie'
import { t } from 'localization'
import { logViewDetail } from 'mParticle/ecommerceMParticleEvents'
import { logSpecificPageView } from 'mParticle/mParticleService'
import { NextPage } from 'next'
import ErrorPage from 'pages/_error'
import { ProductTemplateLayout } from 'productTemplate'
import ProductBreadcrumbs from 'productTemplate/components/ProductBreadcrumbs'
import { ProductRecommendations } from 'productTemplate/components/ProductRecommendations'
import { ProductTemplateContextProvider } from 'productTemplate/components/ProductTemplateContextProvider'
import ProductTemplateStructuredData from 'productTemplate/components/ProductTemplateStructuredData'
import SeoBreadcrumbList from 'productTemplate/components/SeoBreadcrumbList'
import Divider from 'shared/components/Divider'
import PageContextProvider from 'shared/contexts/PageContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { STORAGE } from 'shared/enums/SitePreferences'
import SpecificPageTrackingEvents from 'shared/enums/SpecificPageTrackingEvents'
import { renderUntil } from 'shared/hocs/render'
import { useAuth } from 'shared/hooks/useAuth'
import { useUser } from 'shared/hooks/useUser'
import { Breadcrumb } from 'shared/types/Breadcrumb'
import { startCase } from 'shared/utils/formatting-utils'
import { GetPDPServerSideProps } from 'shared/utils/getPDPServerSideProps'
import { trackSnapchatPageView } from 'tracking/snapchatService'

type ProductTemplatePageProps = Omit<GetPDPServerSideProps, 'isPdp'>
type MobileBreadcrumbs = {
  breadcrumbPath?: Breadcrumb[] | null
  name: string
}

const TP = 'productTemplate.ProductTemplatePage'

export const ProductTemplatePage: NextPage<ProductTemplatePageProps> = ({
  errorStatus,
  hostname,
  locale,
  pathname,
  productTemplate,
  urlSize,
  isAutomation,
}) => {
  const { currentUserType } = useUser()
  const { logoutUser } = useAuth()
  const { trackTikTokPDPView } = useTikTokPixelTrackEvent()

  const areExperimentsEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const isCheckoutRolloutEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_CHECKOUT_PLATFORM_ROLLOUT)
  const isCheckoutPlatformInternalTestEnabled = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_CHECKOUT_PLATFORM_INTERNAL_TEST,
  )

  const checkoutPlatformExperiment: EXPERIMENT_NAME = 'web_fc_checkout_platform'

  const isCheckoutPlatformExperimentEnabled =
    areExperimentsEnabled && isCheckoutRolloutEnabled && !isAutomation

  const { experimentState } = useStartExperiment(
    checkoutPlatformExperiment,
    !isCheckoutPlatformExperimentEnabled,
  )
  useLogExperiment(checkoutPlatformExperiment, !isCheckoutPlatformExperimentEnabled)

  useEffect(() => {
    if (productTemplate) {
      const { name, brandName, sku, slug } = productTemplate

      const delayTrackTikTokPDPView = debounce(() => {
        trackTikTokPDPView({
          contentCategory: brandName,
          contentId: sku,
          contentName: name,
          price: productTemplate?.newLowestPrice?.value,
        })
      }, 500)

      logSpecificPageView({
        slug,
        pageName: SpecificPageTrackingEvents.ProductTemplatePage,
        slugType: 'product_template',
      })
      if (productTemplate?.newLowestPrice?.value) {
        logViewDetail({
          name,
          brandName: brandName,
          sku,
          priceCents: productTemplate?.newLowestPrice?.value,
        })
      }
      trackSnapchatPageView({ sku })
      delayTrackTikTokPDPView()
    }
  }, [productTemplate?.id])

  useEffect(() => {
    const cohort =
      isCheckoutPlatformInternalTestEnabled || experimentState?.groupAssigned === 'variant'
        ? '1'
        : '0'
    Cookies.set(STORAGE.CHECKOUT_PLATFORM_COHORT, cohort)
  }, [experimentState, isCheckoutPlatformInternalTestEnabled])

  /**
   * BUY1-354
   * Log guest users out when they reach here so that checkout is guaranteed to start
   * fresh and not have previous guest user logged in.
   */
  useEffect(() => {
    if (currentUserType === 'registeredGuest') {
      logoutUser()
    }
  }, [currentUserType])

  if (errorStatus || !productTemplate) {
    const status = errorStatus || 404
    return <ErrorPage statusCode={status} />
  }

  const { name, brandName = '', sku = '', details = '', breadcrumbPath = [] } = productTemplate

  return (
    <PageContextProvider value={SpecificPageTrackingEvents.ProductTemplatePage}>
      <ProductTemplateContextProvider productTemplate={productTemplate} urlSize={urlSize}>
        <Head data-testid="product-template-page">
          <title>
            {t(`${TP}.pageTitle`, {
              defaultValue: '{name} | Flight Club',
              name: buildTitle([startCase(name), startCase(brandName), sku, toLower(details)]),
            })}
          </title>
          <meta
            name="description"
            content={buildPDPMetaDescription({
              brand: brandName,
              category: productTemplate?.productCategory,
              name,
            })}
          />
          <meta name="robots" content="max-image-preview:large" />
        </Head>
        <SeoBreadcrumbList
          listData={filter(SeoBreadcrumbList.fragments.seoBreadcrumbList, {
            breadcrumbPath,
            hostname: hostname!,
            name,
            pathname: pathname!,
          })}
        />
        <ProductTemplateStructuredData locale={locale} productTemplate={productTemplate} />
        <ProductTemplateLayout />
        <ProductRecommendations productTemplateId={productTemplate.id} isPDP={true} />
        <MobileBreadcrumbs breadcrumbPath={breadcrumbPath} name={name} />
      </ProductTemplateContextProvider>
    </PageContextProvider>
  )
}

const MobileBreadcrumbComponent = ({ breadcrumbPath, name }: MobileBreadcrumbs) => {
  return (
    <>
      <Divider data-testid="PDPBreadcrumbsDivider" />
      <ProductBreadcrumbs breadcrumbPath={breadcrumbPath} name={name} />
    </>
  )
}

const MobileBreadcrumbs = renderUntil.large(MobileBreadcrumbComponent)
